/**
 * Per-kind validators + payload shapers for the FormatInferrer Custom-spec
 * dispatch path. Single source of truth for what the frontend sends per
 * detector kind.
 *
 * Why this exists
 * ---------------
 * Until v0.5 Stage A, the HitlQueue's Custom-input path had a single-string
 * payload (`{value: "<input>"}`) that only matched the cleanser contract for
 * `regex_mask`. For every other detector kind (`boolean_labels`, `enum`,
 * `date_format`, `numeric_precision`) the cleansers read different keys
 * (`true_label`/`false_label`, `values`, `input_format`,
 * `decimal_places`+`thousands_separator`) — the reviewer's input was
 * silently discarded, with the cleanser falling through to its default
 * canonicalisation.
 *
 * That bug was production-affecting from v0.2.1 (FormatInferrer's
 * introduction) until v0.5.0. v0.5 Stage A closes it by introducing this
 * module + the matching detector-aware UI in `CustomSpecInput.tsx`.
 *
 * The cleanser-side keys this module produces are asserted by
 * `tests/contract/test_format_spec_contract.py` against the actual
 * cleanser code paths — adding a new detector kind without all three
 * (UI shape here, validator here, cleanser consumer in `cleaners/`) fails
 * the contract test.
 *
 * See `docs/v0.5_DEV_PLAN.md` §3.1 + `docs/DECISIONS.md` 2026-04-25
 * Stage A entry for the design rationale.
 */

/**
 * The five detector kinds the Custom path supports. `pattern_none` and
 * `none` are FormatSpec kinds for "no enforcement" paths and don't have
 * a Custom-input UI; they're handled by the "No format required" button.
 */
export type CustomSpecKind =
  | 'regex_mask'
  | 'boolean_labels'
  | 'enum'
  | 'date_format'
  | 'numeric_precision';

/**
 * Free-form reviewer input held in the component's local state. The shape
 * is per-kind so each detector can render its own fields without leaking
 * into the others. All fields are strings (raw HTML input values); the
 * validator parses + validates before dispatch.
 */
export type CustomSpecDraft =
  | { kind: 'regex_mask'; pattern: string }
  | { kind: 'boolean_labels'; trueLabel: string; falseLabel: string }
  | { kind: 'enum'; valuesCsv: string }
  | { kind: 'date_format'; inputFormat: string }
  | { kind: 'numeric_precision'; decimalPlaces: string; thousandsSeparator: boolean };

/**
 * Validation result. `ok` carries the parsed spec body ready to ship in
 * the `choose_format_spec` payload's `spec` field. `error` carries a
 * reviewer-facing message rendered inline below the input.
 */
export type ValidationResult =
  | { ok: true; spec: Record<string, unknown> }
  | { ok: false; error: string };

/**
 * Empty draft for a given kind — used when the reviewer first picks
 * the Custom radio and the component needs starting state.
 */
export function emptyDraft(kind: CustomSpecKind): CustomSpecDraft {
  switch (kind) {
    case 'regex_mask':
      return { kind, pattern: '' };
    case 'boolean_labels':
      return { kind, trueLabel: '', falseLabel: '' };
    case 'enum':
      return { kind, valuesCsv: '' };
    case 'date_format':
      return { kind, inputFormat: '' };
    case 'numeric_precision':
      return { kind, decimalPlaces: '', thousandsSeparator: false };
  }
}

/**
 * Parse a reviewer's draft into the wire-format spec body, or return a
 * reviewer-facing error string. The returned shape MUST match what the
 * matching cleanser reads (see `tests/contract/test_format_spec_contract.py`).
 *
 * Cleanser-side mappings as of v0.5 Stage A:
 *   * `regex_mask`        → `cleaners/string_cleaner.py:377` reads `spec.pattern`
 *   * `enum`              → `cleaners/categorical_normaliser.py:118` reads `spec.values`
 *   * `date_format`       → `cleaners/date_cleaner.py:251` reads `spec.input_format`
 *   * `numeric_precision` → `cleaners/numeric_cleaner.py:370` reads `spec.decimal_places` + `spec.thousands_separator`
 *   * `boolean_labels`    → `cleaners/boolean_cleaner.py:132` reads `spec.true_label` + `spec.false_label`
 */
export function validateCustomSpec(draft: CustomSpecDraft): ValidationResult {
  switch (draft.kind) {
    case 'regex_mask': {
      const pattern = draft.pattern.trim();
      if (!pattern) {
        return { ok: false, error: 'Enter a regex pattern (e.g. [A-Z]{3}-\\d+).' };
      }
      // Validate regex compiles. We assign to a discard variable so
      // a stricter linter doesn't trip on `new RegExp(...)` as a
      // standalone expression.
      try {
        const _compiled = new RegExp(pattern);
        void _compiled;
      } catch (e) {
        return { ok: false, error: `Invalid regex: ${(e as Error).message}` };
      }
      return { ok: true, spec: { pattern } };
    }

    case 'boolean_labels': {
      const trueLabel = draft.trueLabel.trim();
      const falseLabel = draft.falseLabel.trim();
      if (!trueLabel || !falseLabel) {
        return {
          ok: false,
          error: 'Enter both labels (e.g. "Y" and "N", or "1" and "0").',
        };
      }
      if (trueLabel === falseLabel) {
        return { ok: false, error: 'True and false labels must differ.' };
      }
      return { ok: true, spec: { true_label: trueLabel, false_label: falseLabel } };
    }

    case 'enum': {
      // Comma-separated. Trim each entry; drop empties; deduplicate
      // while preserving order (so "Active, Pending, Active" → ["Active",
      // "Pending"]). Categorical_normaliser reads `spec.values` as a
      // list[str] — order doesn't matter to the cleanser, but we keep
      // the reviewer's order so the chip preview reads predictably.
      const raw = draft.valuesCsv.split(',').map((v) => v.trim()).filter(Boolean);
      const seen = new Set<string>();
      const values: string[] = [];
      for (const v of raw) {
        if (!seen.has(v)) {
          seen.add(v);
          values.push(v);
        }
      }
      if (values.length < 2) {
        return {
          ok: false,
          error: 'Enter at least two comma-separated values (e.g. "Active, Inactive").',
        };
      }
      return { ok: true, spec: { values } };
    }

    case 'date_format': {
      const inputFormat = draft.inputFormat.trim();
      if (!inputFormat) {
        return {
          ok: false,
          error: 'Enter a strftime-style format (e.g. %Y-%m-%d or %d/%m/%Y).',
        };
      }
      // Sanity-check the format contains at least one strftime directive.
      // We don't try to fully validate strftime here — the cleanser will
      // surface anomaly flags on rows that don't parse.
      if (!/%[YmdHMSjyaAbBpzZUWcxX]/.test(inputFormat)) {
        return {
          ok: false,
          error: 'Format must contain at least one %Y/%m/%d-style directive.',
        };
      }
      // numeric_precision-style key, but for dates: cleanser reads
      // `input_format`. Output_format is left null — the cleanser
      // applies the run-level `date_output_format` preference.
      return {
        ok: true,
        spec: { input_format: inputFormat, output_format: null },
      };
    }

    case 'numeric_precision': {
      const dpRaw = draft.decimalPlaces.trim();
      if (!dpRaw) {
        return { ok: false, error: 'Enter the number of decimal places (0–10).' };
      }
      // Strict integer parse — no NaN passthrough, no float input.
      if (!/^\d+$/.test(dpRaw)) {
        return { ok: false, error: 'Decimal places must be a whole number.' };
      }
      const decimalPlaces = parseInt(dpRaw, 10);
      if (decimalPlaces < 0 || decimalPlaces > 10) {
        return { ok: false, error: 'Decimal places must be between 0 and 10.' };
      }
      return {
        ok: true,
        spec: {
          decimal_places: decimalPlaces,
          thousands_separator: draft.thousandsSeparator,
        },
      };
    }
  }
}

/**
 * Discriminator: does this candidate kind have a Custom-input UI?
 *
 * `pattern_none` (no detector match) and `none` (reviewer pass-through)
 * never appear in the Custom path — they're the result of NOT picking a
 * format. The HitlQueue's Custom radio is hidden when the candidate's
 * kind isn't in this set.
 */
export function isCustomSpecKind(kind: string): kind is CustomSpecKind {
  return (
    kind === 'regex_mask'
    || kind === 'boolean_labels'
    || kind === 'enum'
    || kind === 'date_format'
    || kind === 'numeric_precision'
  );
}
